/**
 * 분할결제 API — 좌석별 Bill (menu #814 / admin #2204)
 *
 * ## 계약의 단일 출처는 admin 이다
 *
 * 필드명·의미·상태값은 전부 admin 코드가 정한다. 여기서 임의로 해석하지 않는다 —
 * 갈리면 화면이 원장과 다른 이야기를 한다.
 *
 * ## 두 개의 토큰
 *
 * - `X-Session-Token` — 이 테이블 세션. `apiClient` 가 자동 부착한다
 * - `X-Comensal-Token` — **이 사람**. 분할결제에서만 쓰며 여기서 명시 부착한다
 *
 * 세션 토큰만으로는 "누가" 를 알 수 없다. QR 사진 한 장이면 남의 테이블 세션 토큰을
 * 얻을 수 있으므로, 지분을 주고받는 연산은 사람 단위 토큰을 요구한다.
 */
import { apiClient, validatePathParam } from './client';

/** 좌석 참여자 — admin `SessionJoinController::present()` 와 필드가 일치해야 한다. */
export interface Comensal {
  id: number;
  table_session_id: number;
  role: string;
  role_label: string;
  join_status: string;
  join_status_label: string;
  allocation_mode: string;
  allocation_mode_label: string;
  display_name: string | null;
  /** 이 사람이 자기 Bill 을 따로 갖는가(D4 대표 합산 default 의 반대) */
  requires_own_bill: boolean;
  joined_at: string | null;
  /** entry 합류 완료 시 한 번 전달되는 실제 테이블 세션 토큰. */
  session_token?: string;
  /** entry route에서만 required. device-bound entry의 서버 권위 절대 만료시각. */
  entry_expires_at?: string;
  session?: {
    guest_count: number;
    joined_count: number;
    split_payment_available: boolean;
    has_split_ledger: boolean;
    join_strategy: 'code' | 'host_approval';
  };
}

/** `scan` 응답에만 실린다 — 이후 모든 요청의 `X-Comensal-Token` 이다. */
export interface ScannedComensal extends Comensal {
  device_token: string;
}

export interface EntryComensal extends Comensal {
  entry_expires_at: string;
}

export interface ScannedEntryComensal extends ScannedComensal {
  entry_expires_at: string;
}

/**
 * 계산서의 결제 상태 — 서버가 접어서 내리는 3분류.
 *
 * `refunded` 는 `pagado` 로 접힌다(낸 뒤 되돌려받은 것이지 안 낸 것이 아니다).
 * 환불 사실 자체는 `status`/`status_label` 이 싣는다.
 */
export type BillPaymentState = 'pagado' | 'en_caja' | 'pendiente';

/** 계산서 — admin `SessionBillContract::presentBill()`. */
export interface SessionBill {
  id: number;
  comensal_id: number | null;
  comensal_name: string | null;
  sequence_no: number;
  /** 잔여 Bill 은 특정 사람의 것이 아니다 — 세션 전체의 몫이다. */
  is_residual: boolean;
  status: string;
  status_label: string;
  /**
   * 서버가 **이미 접어서** 내리는 3분류 (admin `SessionBillContract::paymentState()`).
   *
   * Bill 상태 5종을 FE 가 다시 접으면, 상태가 하나 늘 때마다 cross-repo 배포가 필요하다.
   * 화면이 실제로 구분하는 것은 "아직 안 냄 / 계산 중 / 냈음" 셋뿐이다.
   */
  payment_state: BillPaymentState;
  subtotal_amount: number;
  tip_amount: number;
  /** 서버가 계산한 Bill 총액. FE에서 subtotal+tip을 다시 더하지 않는다. */
  total_amount: number;
  /** 지분을 주고받을 수 있는 상태인가(`open` 만 참) */
  accepts_transfer: boolean;
}

export interface SessionBillLimits {
  /** 부분 이전 최소 금액(centavo). D10 = MX$50 */
  minimum_partial_transfer_minor: number;
  /** 잔여 Bill 에 남은 금액(centavo) */
  residual_available_minor: number;
}

export interface SessionBillItemClaim {
  bill_id: number;
  comensal_id: number | null;
  comensal_name: string | null;
  /** 서버가 계산한 실제 귀속 금액(MXN). 화면에서 지분을 재계산하지 않는다. */
  amount: number;
  share_ppm: number;
}

export interface SessionBillItem {
  order_item_id: number;
  order_id: number;
  name: string;
  quantity: number;
  unit_price: number;
  total_amount: number;
  residual_amount: number;
  fully_claimed: boolean;
  claims: SessionBillItemClaim[];
}

export interface SessionBillParticipants {
  /** 합류 완료한 참여자 수(대표 포함). */
  joined_count: number;
}

export interface SessionBillsResponse {
  /** 현재 store 정책 AND release gate. false면 원장은 읽되 claim은 금지한다. */
  split_payment_available: boolean;
  /** residual 외 개인 Bill이 한 번이라도 생긴 원장인가. OFF 전환 시 legacy fallback 금지 기준. */
  has_split_ledger: boolean;
  bills: SessionBill[];
  /**
   * 내 **미수납** Bill. 없으면 null.
   *
   * settled 후 다시 claim 하면 새 Bill 이 생기므로, 이 값은 "지금 내가 채우고 있는
   * 계산서" 를 가리킨다.
   */
  my_bill_id: number | null;
  /**
   * 잔여 Bill — **`bills` 에는 들어 있지 않다**(스테이징 E2E 확인).
   *
   * `bills` 는 사람별 Bill 만 담는다. 진행·합계를 낼 때 잔여를 빼먹으면, 아무도 아직
   * 가져가지 않은 세션에서 총액이 0 이 되어 "다 냈다" 로 보인다.
   */
  residual: SessionBill | null;
  limits: SessionBillLimits;
  items: SessionBillItem[];
  /** admin #2266. 구버전 응답은 필드가 없어 UI가 Bill 수로 안전하게 폴백한다. */
  participants?: SessionBillParticipants;
}

export interface BillReceiptPayment {
  id: number;
  order_id: number;
  method: string;
  method_label: string;
  status: string;
  status_label: string;
  amount: string;
  currency: string;
  points_amount: string;
  refund_amount: string;
  card_brand: string | null;
  card_last_four: string | null;
  paid_at: string | null;
  transaction_id: string | null;
}

export interface MembershipRewardPreview {
  earn_rate: number;
  missed_points: number;
  /** 일반 주문 완료 응답에는 없을 수 있고 Bill 영수증에는 항상 false로 온다. */
  retroactive_credit_available?: boolean;
}

export interface BillMembershipRewardPreview extends MembershipRewardPreview {
  /** Bill 영수증은 가입 유인용 추정치이며 소급 적립 약속이 아니다. */
  retroactive_credit_available: false;
}

/** BillLine 기준 영수증 항목. 잔여/다른 좌석의 claim 정보는 포함하지 않는다. */
export interface SessionBillReceiptItem {
  order_id: number;
  order_item_id: number;
  name: string;
  /** 이 Bill에 귀속된 금액의 서버 권위 값. */
  amount: number;
  share_ppm: number;
}

export interface SessionBillReceiptAmounts {
  subtotal: number;
  tip: number;
  total: number;
  paid: number;
  refunded: number;
  currency: string;
}

export interface SessionBillReceipt {
  bill: SessionBill;
  items: SessionBillReceiptItem[];
  payments: BillReceiptPayment[];
  amounts: SessionBillReceiptAmounts;
  membership_reward_preview: BillMembershipRewardPreview | null;
}

export interface SessionChargeBundleStatus {
  bundle: {
    id: number;
    status: string;
    total_amount: string;
    bill_id: number | null;
    is_successful: boolean;
    is_final: boolean;
    /** 결제 polling의 종료 권위. paid에서도 is_final은 false일 수 있다. */
    polling_complete: boolean;
    updated_at: string;
  };
  payments: Array<{
    id: number;
    status: string;
    amount: string;
  }>;
}

/**
 * 지분 가져오기 요청 — 세 형태가 **배타**다(admin `ClaimBillShareRequest`).
 *
 * - 항목 전량: `order_item_id`
 * - 항목 분수: `order_item_id` + `numerator`/`denominator`
 * - 금액 지정: `amount_minor`
 */
/**
 * 합류자의 몫을 어떻게 둘 것인가 (admin `BillAllocationMode`).
 *
 * - `covered` — 대표가 흡수한다(D4 기본값, 현재 동작과 같다)
 * - `split` — 이 사람의 Bill 을 따로 만든다
 */
export type BillAllocationMode = 'covered' | 'split';

export type ClaimShareRequest =
  | { order_item_id: number }
  | { order_item_id: number; numerator: number; denominator: number }
  | { amount_minor: number }
  /** 서버가 한 트랜잭션으로 세션 총액 1/N 목표와 내 Bill 차액을 적용한다. */
  | { equal_denominator: number };

function comensalHeaders(deviceToken: string): Record<string, string> {
  return { 'X-Comensal-Token': deviceToken };
}

function positivePathId(value: number, name: string): string {
  if (!Number.isInteger(value) || value < 1) {
    throw new TypeError(`${name} must be a positive integer`);
  }
  return validatePathParam(String(value), name);
}

export const splitPaymentApi = {
  /**
   * QR 스캔 — 아직 합류한 것은 아니다.
   *
   * D2 결정에 따라 스캔만 한 사람(Invitado)은 **메뉴 열람만** 할 수 있다.
   * 장바구니·주문은 합류 후다.
   */
  async scan(
    sessionToken: string,
    displayName?: string,
  ): Promise<ScannedComensal> {
    const safeToken = validatePathParam(sessionToken, 'token');
    const response = await apiClient.post<{ data: ScannedComensal }>(
      `/api/v1/table-sessions/${safeToken}/scan`,
      displayName === undefined ? {} : { display_name: displayName },
    );

    return response.data;
  },

  /** 저장된 디바이스 토큰으로 새로고침 뒤 역할·합류 상태를 복구한다. */
  async current(sessionToken: string, deviceToken: string): Promise<Comensal> {
    const safeToken = validatePathParam(sessionToken, 'token');
    const response = await apiClient.get<{ data: Comensal }>(
      `/api/v1/table-sessions/${safeToken}/comensal`,
      { headers: comensalHeaders(deviceToken) }
    );

    return response.data;
  },

  /** 활성 split 세션의 짧은 QR entry에서 view-only 참여자를 만든다. */
  async scanEntry(entryToken: string, displayName?: string): Promise<ScannedEntryComensal> {
    const safeEntry = validatePathParam(entryToken, 'entry');
    const response = await apiClient.post<{ data: ScannedEntryComensal }>(
      `/api/v1/table-session-entries/${safeEntry}/scan`,
      displayName === undefined ? {} : { display_name: displayName },
    );
    return response.data;
  },

  /** entry 응답 유실과 대표 승인 polling을 동일 device token으로 복구한다. */
  async currentEntry(entryToken: string, deviceToken: string): Promise<EntryComensal> {
    const safeEntry = validatePathParam(entryToken, 'entry');
    const response = await apiClient.get<{ data: EntryComensal }>(
      `/api/v1/table-session-entries/${safeEntry}/comensal`,
      { headers: comensalHeaders(deviceToken) },
    );
    return response.data;
  },

  /** 코드 전략 entry 합류. 성공 응답의 session_token으로 live 세션을 승격한다. */
  async joinEntry(
    entryToken: string,
    deviceToken: string,
    payload: { code: string },
  ): Promise<EntryComensal> {
    const safeEntry = validatePathParam(entryToken, 'entry');
    const response = await apiClient.post<{ data: EntryComensal }>(
      `/api/v1/table-session-entries/${safeEntry}/join`,
      payload,
      { headers: comensalHeaders(deviceToken) },
    );
    return response.data;
  },

  /** 합류 요청 — 매장 정책에 따라 코드 입력 또는 대표 승인이 필요하다. */
  async join(
    sessionToken: string,
    deviceToken: string,
    payload: { code: string }
  ): Promise<Comensal> {
    const safeToken = validatePathParam(sessionToken, 'token');
    const response = await apiClient.post<{ data: Comensal }>(
      `/api/v1/table-sessions/${safeToken}/join`,
      payload,
      { headers: comensalHeaders(deviceToken) }
    );

    return response.data;
  },

  /** 대표가 처리할 승인 대기 목록. 권한/전략 불일치는 서버가 거절한다. */
  async listJoinRequests(sessionToken: string, deviceToken: string): Promise<Comensal[]> {
    const safeToken = validatePathParam(sessionToken, 'token');
    const response = await apiClient.get<{ data: Comensal[] }>(
      `/api/v1/table-sessions/${safeToken}/join-requests`,
      { headers: comensalHeaders(deviceToken) }
    );

    return response.data;
  },

  /**
   * 대표가 합류 요청을 승인한다(D1 대표승인 전략).
   *
   * `allocationMode` 가 D4 의 핵심이다 — 배분을 결제 시점이 아니라 **합류 승인
   * 시점**에, 리스크를 지는 대표가 정한다. 생략하면 서버가 커버(대표 합산)로 보고,
   * 무행동 시 현재 동작과 같아진다.
   *
   * 다만 이건 **초기 배분값일 뿐**이다. 대표가 커버로 세팅해도 각 참여자는 스스로
   * "내 것만 낼게" 를 발동할 수 있다(D5 고객 주도).
   */
  async approveJoin(
    sessionToken: string,
    deviceToken: string,
    comensalId: number,
    allocationMode?: BillAllocationMode
  ): Promise<Comensal> {
    const safeToken = validatePathParam(sessionToken, 'token');
    const safeComensalId = positivePathId(comensalId, 'comensalId');
    const response = await apiClient.post<{ data: Comensal }>(
      `/api/v1/table-sessions/${safeToken}/join-requests/${safeComensalId}/approve`,
      allocationMode === undefined ? {} : { allocation_mode: allocationMode },
      { headers: comensalHeaders(deviceToken) }
    );

    return response.data;
  },

  /** 대표가 합류 요청을 거절한다. */
  async rejectJoin(
    sessionToken: string,
    deviceToken: string,
    comensalId: number
  ): Promise<Comensal> {
    const safeToken = validatePathParam(sessionToken, 'token');
    const safeComensalId = positivePathId(comensalId, 'comensalId');
    const response = await apiClient.post<{ data: Comensal }>(
      `/api/v1/table-sessions/${safeToken}/join-requests/${safeComensalId}/reject`,
      {},
      { headers: comensalHeaders(deviceToken) }
    );

    return response.data;
  },

  /**
   * 세션의 Bill 목록.
   *
   * **403 은 "분할결제가 이 매장에서 꺼져 있다"** 는 뜻이다(매장 정책 또는 개발 게이트).
   * 오류가 아니라 상태이므로 호출부가 그렇게 다뤄야 한다.
   */
  async listBills(sessionToken: string, deviceToken: string): Promise<SessionBillsResponse> {
    const safeToken = validatePathParam(sessionToken, 'token');
    const response = await apiClient.get<{ data: SessionBillsResponse }>(
      `/api/v1/table-sessions/${safeToken}/bills`,
      { headers: comensalHeaders(deviceToken) }
    );

    return response.data;
  },

  /**
   * 지분 가져오기.
   *
   * 409 는 **다른 손님이 먼저 가져갔다**는 뜻이다(낙관적 잠금). 재조회 후 다시 시도해야
   * 하며, 조용히 재시도하면 손님이 의도하지 않은 항목을 집을 수 있다.
   */
  async claim(
    sessionToken: string,
    deviceToken: string,
    request: ClaimShareRequest
  ): Promise<SessionBill> {
    const safeToken = validatePathParam(sessionToken, 'token');
    const response = await apiClient.post<{ data: SessionBill }>(
      `/api/v1/table-sessions/${safeToken}/bills/claims`,
      request,
      { headers: comensalHeaders(deviceToken) }
    );

    return response.data;
  },

  /** 본인 Bill 영수증. Comensal 소유 검증은 서버가 한다. */
  async getBillReceipt(
    sessionToken: string,
    deviceToken: string,
    billId: number
  ): Promise<SessionBillReceipt> {
    const safeToken = validatePathParam(sessionToken, 'token');
    const safeBillId = positivePathId(billId, 'billId');
    const response = await apiClient.get<{ data: SessionBillReceipt }>(
      `/api/v1/table-sessions/${safeToken}/bills/${safeBillId}/receipt`,
      { headers: comensalHeaders(deviceToken) }
    );

    return response.data;
  },

  /** Comensal 세션 번들 3DS 결과. 대표 order/payment 상태를 대신하는 서버 권위 조회다. */
  async getChargeBundleStatus(
    sessionToken: string,
    deviceToken: string,
    bundleId: number,
  ): Promise<SessionChargeBundleStatus> {
    const safeToken = validatePathParam(sessionToken, 'token');
    const safeBundleId = positivePathId(bundleId, 'bundleId');
    return apiClient.get<SessionChargeBundleStatus>(
      `/api/v1/table-sessions/${safeToken}/charge-bundles/${safeBundleId}/status`,
      { headers: comensalHeaders(deviceToken) },
    );
  },
};
